import React, { useRef, useState, useCallback, useEffect } from 'react';
import * as fabric from 'fabric';
import { MAX_HISTORY_STATES, HISTORY_DEBOUNCE_MS } from '../constants';

export function useCanvasHistory(
  fabricCanvasRef: React.RefObject<fabric.Canvas | null>,
  isUpdatingHistoryRef: React.MutableRefObject<boolean>,
  onChange?: () => void
) {
  const historyRef = useRef<string[]>([]);
  const historyIndexRef = useRef<number>(-1);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Core function to push state to history stack with state limit
  const pushState = useCallback((jsonStr: string) => {
    const currentIndex = historyIndexRef.current;
    const history = historyRef.current;

    // Truncate redo stack when new state is added
    const newHistory = history.slice(0, currentIndex + 1);
    newHistory.push(jsonStr);

    // Limit memory footprint to MAX_HISTORY_STATES (slice last 50 states)
    if (newHistory.length > MAX_HISTORY_STATES) {
      newHistory.shift();
    }

    historyRef.current = newHistory;
    historyIndexRef.current = newHistory.length - 1;

    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(false);
  }, []);

  // Save history state immediately
  const saveHistoryImmediate = useCallback((canvasInstance?: fabric.Canvas) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const canvas = canvasInstance || fabricCanvasRef.current;
    if (!canvas || isUpdatingHistoryRef.current) return;

    try {
      const jsonStr = JSON.stringify(canvas.toJSON());
      pushState(jsonStr);
      if (onChange) onChange();
    } catch (e) {
      console.error('Failed to serialize canvas state:', e);
    }
  }, [fabricCanvasRef, isUpdatingHistoryRef, pushState, onChange]);

  // Debounced history save to prevent UI freeze during continuous movement or styling
  const saveHistoryDebounced = useCallback((canvasInstance?: fabric.Canvas) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      saveHistoryImmediate(canvasInstance);
    }, HISTORY_DEBOUNCE_MS);
  }, [saveHistoryImmediate]);

  const undo = useCallback(async () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || historyIndexRef.current <= 0) return;

    isUpdatingHistoryRef.current = true;
    historyIndexRef.current -= 1;
    const previousState = historyRef.current[historyIndexRef.current];

    try {
      const parsed = JSON.parse(previousState);
      const res = canvas.loadFromJSON(parsed);
      if (res && typeof (res as any).then === 'function') {
        await res;
      }
      canvas.renderAll();
    } catch (e) {
      console.error('Error during undo operation:', e);
    } finally {
      isUpdatingHistoryRef.current = false;
      setCanUndo(historyIndexRef.current > 0);
      setCanRedo(historyIndexRef.current < historyRef.current.length - 1);
      if (onChange) onChange();
    }
  }, [fabricCanvasRef, isUpdatingHistoryRef, onChange]);

  const redo = useCallback(async () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || historyIndexRef.current >= historyRef.current.length - 1) return;

    isUpdatingHistoryRef.current = true;
    historyIndexRef.current += 1;
    const nextState = historyRef.current[historyIndexRef.current];

    try {
      const parsed = JSON.parse(nextState);
      const res = canvas.loadFromJSON(parsed);
      if (res && typeof (res as any).then === 'function') {
        await res;
      }
      canvas.renderAll();
    } catch (e) {
      console.error('Error during redo operation:', e);
    } finally {
      isUpdatingHistoryRef.current = false;
      setCanUndo(historyIndexRef.current > 0);
      setCanRedo(historyIndexRef.current < historyRef.current.length - 1);
      if (onChange) onChange();
    }
  }, [fabricCanvasRef, isUpdatingHistoryRef, onChange]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    canUndo,
    canRedo,
    saveHistoryImmediate,
    saveHistoryDebounced,
    undo,
    redo,
  };
}
